import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

const BASE = "/transfer-certificate"

export type TCStudent = {
  studentId: string
  admissionId: string
  grNumber: string
  firstName: string
  lastName: string
  fatherName: string
  placeOfBirth: string
  dateOfBirth: string | null
  dateOfAdmission: string | null
  previousSchool: string
  campusName: string
  sessionTitle: string
  systemName: string
  className: string
  sectionName: string
  groupName: string
  // Ids the fee-collection modal needs to open a payment against this child.
  enrollmentId?: string | null
  sessionId?: string | null
  classId?: string | null
  sectionId?: string | null
  systemId?: string | null
  status: string
  // Worked out from paid invoices — the office can still correct it.
  feePaidUpTo: string
  // Where the student stands on fees. Not printed; shown so whoever issues
  // the certificate is told rather than left to guess at an empty box.
  feeStatus?: { label: string; outstanding: number }
  // The Transfer Fee for this student's SYSTEM, not their campus — North
  // Nazimabad has one for O-Level and another for AKU. When `configured` is
  // false nobody has set one up, and the certificate is blocked until they do.
  transferFee?: { configured: boolean; amount: number; paid: boolean; name?: string; feeTypeId?: string }
  existingCertificates: { _id: string; serialNo: string; issueDate: string; status: string }[]
}

export type TransferCertificate = {
  _id: string
  serialNo: string
  student: string
  grNumber: string
  firstName: string
  lastName: string
  fatherName: string
  placeOfBirth: string
  dateOfBirth: string | null
  dateOfAdmission: string | null
  previousSchool: string
  className: string
  sectionName: string
  systemName: string
  groupName: string
  campusName: string
  sessionTitle: string
  classAttendingNow: string
  classProgress: string
  dateOfLeaving: string | null
  reasonForLeaving: string
  reasonForLeavingOther: string
  progress: string
  progressOther: string
  conduct: string
  conductOther: string
  feePaidUpTo: string
  issuedByName: string
  issueDate: string
  status: "issued" | "cancelled"
  createdAt: string
}

export const listTransferCertificates = async (params: {
  branchId?: string
  sessionId?: string
  status?: string
  search?: string
  page?: number
  limit?: number
}): Promise<{
  data: TransferCertificate[]
  pagination: { total: number; page: number; limit: number; totalPages: number }
}> => {
  const res = await apiClient({ method: "GET", url: appendQueryParams(BASE, params) })
  return res.data
}

// Look a student up by GR — the form comes back already filled in.
export const findStudentForTC = async (
  grNumber: string
): Promise<{ success: boolean; data: TCStudent }> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/student/${grNumber}` })
  return res.data
}

export const getTransferCertificate = async (
  id: string
): Promise<{ data: TransferCertificate }> => {
  const res = await apiClient({ method: "GET", url: `${BASE}/${id}` })
  return res.data
}

export const issueTransferCertificate = async (data: Record<string, any>) => {
  const res = await apiClient({ method: "POST", url: BASE, data })
  return res.data
}

export const updateTransferCertificate = async (id: string, data: Record<string, any>) => {
  const res = await apiClient({ method: "PUT", url: `${BASE}/${id}`, data })
  return res.data
}

// Withdraws the certificate and puts the student back on the rolls. The row
// stays — a gap in the serials would be unexplainable.
export const cancelTransferCertificate = async (id: string) => {
  const res = await apiClient({ method: "DELETE", url: `${BASE}/${id}` })
  return res.data
}
